import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { IconArrowLeft, IconFilter, IconPlus, IconTrash } from '../components/icons';
import { Empty, Field, Skeleton, useAsync, useToast } from '../components/ui';
import { api, AttributeDef, AttributeType, Segment, SegmentPreview } from '../lib/api';
import { ago, num } from '../lib/format';
import { canWrite, useSession } from '../lib/session';
import { errorText } from './Contacts';
import { t } from '../lib/i18n';

// ---------- the condition model of the builder ----------

type Op =
  | 'eq' | 'neq' | 'in' | 'not_in' | 'gt' | 'gte' | 'lt' | 'lte' | 'between' | 'contains' | 'starts_with'
  | 'exists' | 'not_exists' | 'within_last_days' | 'more_than_days_ago' | 'within_next_days' | 'is_true' | 'is_false';
type Row =
  | { t: 'attribute'; key: string; op: Op; value: string }
  | { t: 'field'; field: 'email' | 'external_id' | 'timezone' | 'locale' | 'created_at'; op: Op; value: string }
  | { t: 'consent'; status: 'granted' | 'withdrawn' | 'unknown' }
  | { t: 'event'; name: string; countOp: 'gte' | 'lte' | 'eq'; count: string; days: string }
  | { t: 'segment'; key: string };
export type Group = { t: 'group'; kind: 'all' | 'any'; items: (Row | Group)[] };

const OP_LABEL: Record<Op, string> = {
  eq: 'равно', neq: 'не равно', in: 'одно из', not_in: 'ни одно из', gt: 'больше', gte: 'не меньше', lt: 'меньше', lte: 'не больше',
  between: 'между', contains: 'содержит', starts_with: 'начинается с', exists: 'заполнено', not_exists: 'не заполнено',
  within_last_days: 'за последние N дней', more_than_days_ago: 'больше N дней назад', within_next_days: 'в ближайшие N дней', is_true: 'да', is_false: 'нет',
};
const OPS_BY_TYPE: Record<AttributeType, Op[]> = {
  string: ['eq', 'neq', 'in', 'not_in', 'contains', 'starts_with', 'exists', 'not_exists'],
  number: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'in', 'not_in', 'exists', 'not_exists'],
  boolean: ['is_true', 'is_false', 'exists', 'not_exists'],
  date: ['within_last_days', 'more_than_days_ago', 'within_next_days', 'eq', 'gt', 'lt', 'between', 'exists', 'not_exists'],
  datetime: ['within_last_days', 'more_than_days_ago', 'within_next_days', 'gt', 'lt', 'between', 'exists', 'not_exists'],
  string_list: ['contains', 'in', 'not_in', 'exists', 'not_exists'],
};
const FIELDS: { id: Extract<Row, { t: 'field' }>['field']; label: string; type: AttributeType }[] = [
  { id: 'email', label: 'Email', type: 'string' },
  { id: 'external_id', label: 'external_id', type: 'string' },
  { id: 'timezone', label: 'Часовой пояс', type: 'string' },
  { id: 'locale', label: 'Локаль', type: 'string' },
  { id: 'created_at', label: 'Дата добавления', type: 'datetime' },
];
const NO_VALUE: Op[] = ['exists', 'not_exists', 'is_true', 'is_false'];
const RELATIVE: Op[] = ['within_last_days', 'more_than_days_ago', 'within_next_days'];
const LIST: Op[] = ['in', 'not_in', 'between'];

function toValue(raw: string, type: AttributeType, op: Op): unknown {
  if (NO_VALUE.includes(op)) return undefined;
  if (RELATIVE.includes(op)) return Number(raw);
  const one = (s: string) => (type === 'number' ? Number(s.trim().replace(',', '.')) : s.trim());
  return LIST.includes(op) ? raw.split(op === 'between' ? /\s*(?:,|—|\.\.)\s*/ : ',').filter((s) => s.trim() !== '').map(one) : one(raw);
}
const fromValue = (v: unknown) => (v === undefined ? '' : Array.isArray(v) ? v.join(', ') : String(v));

function rowToJson(r: Row, types: Map<string, AttributeType>): unknown {
  switch (r.t) {
    case 'attribute': {
      const value = toValue(r.value, types.get(r.key) ?? 'string', r.op);
      return { attribute: r.key, op: r.op, ...(value === undefined ? {} : { value }) };
    }
    case 'field': {
      const type = FIELDS.find((f) => f.id === r.field)?.type ?? 'string';
      const value = toValue(r.value, type, r.op);
      return { field: r.field, op: r.op, ...(value === undefined ? {} : { value }) };
    }
    case 'consent':
      return { consent: 'marketing', status: r.status };
    case 'event': {
      const count = Number(r.count || 1);
      const days = Number(r.days || 0);
      return { event: r.name, ...(r.countOp === 'gte' && count === 1 ? {} : { count: { op: r.countOp, value: count } }), ...(days > 0 ? { within: { days } } : {}) };
    }
    case 'segment':
      return { segment: r.key };
  }
}
export function toJson(g: Group, types: Map<string, AttributeType>): unknown {
  return { [g.kind]: g.items.map((i) => (i.t === 'group' ? toJson(i, types) : rowToJson(i, types))) };
}

/** Builder model of a stored tree, or null when it uses something the builder does not show (not, where, …). */
export function fromJson(raw: unknown, depth = 0): Group | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const kind = 'all' in o ? 'all' : 'any' in o ? 'any' : null;
  if (!kind) {
    const row = rowFromJson(o);
    return row && depth === 0 ? { t: 'group', kind: 'all', items: [row] } : null;
  }
  const list = o[kind];
  if (!Array.isArray(list) || depth > 1) return null;
  const items: (Row | Group)[] = [];
  for (const item of list) {
    const r = item && typeof item === 'object' && ('all' in item || 'any' in item) ? fromJson(item, depth + 1) : rowFromJson(item as Record<string, unknown>);
    if (!r) return null;
    items.push(r);
  }
  return { t: 'group', kind, items };
}
function rowFromJson(o: Record<string, unknown>): Row | null {
  if (!o || typeof o !== 'object') return null;
  if (typeof o.attribute === 'string') return { t: 'attribute', key: o.attribute, op: o.op as Op, value: fromValue(o.value) };
  if (typeof o.field === 'string') return { t: 'field', field: o.field as never, op: o.op as Op, value: fromValue(o.value) };
  if (o.consent === 'marketing') return { t: 'consent', status: o.status as never };
  if (typeof o.segment === 'string') return { t: 'segment', key: o.segment };
  if (typeof o.event === 'string' && !('where' in o)) {
    const c = o.count as { op: 'gte' | 'lte' | 'eq'; value: number } | undefined;
    const w = o.within as { days?: number; hours?: number } | undefined;
    if (w && w.hours) return null;
    return { t: 'event', name: o.event, countOp: c?.op ?? 'gte', count: String(c?.value ?? 1), days: w?.days ? String(w.days) : '' };
  }
  return null;
}

export const newRow = (keys: AttributeDef[]): Row => (keys[0] ? { t: 'attribute', key: keys[0].key, op: OPS_BY_TYPE[keys[0].type][0], value: '' } : { t: 'consent', status: 'granted' });

// ---------- pages ----------

export function SegmentsPage() {
  const { me } = useSession();
  const nav = useNavigate();
  const list = useAsync(() => api.get<Segment[]>('/segments'));
  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1>{t('Сегменты')}</h1>
          <p>{t('Сохранённые условия: атрибуты, согласие, события за период. Список получателей всегда считается заново — сегмент не «замораживает» людей.')}</p>
        </div>
        {canWrite(me) && !me?.support && <button className="btn btn-primary" onClick={() => nav('/segments/new')}><IconPlus size={15} /> {t('Новый сегмент')}</button>}
      </div>
      <div className="card">
        {list.loading ? (
          <div className="card-pad stack"><Skeleton /><Skeleton /></div>
        ) : !list.data?.length ? (
          <Empty icon={<IconFilter size={36} />} title={t('Сегментов пока нет')}>{t('Например: «тариф pro и оплатил за 30 дней» или «есть согласие и не открывал приложение 60 дней».')}</Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>{t('Сегмент')}</th><th>{t('Размер')}</th><th>{t('Ревизия')}</th><th>{t('Изменён')}</th></tr></thead>
              <tbody>
                {list.data.map((s) => (
                  <tr key={s.key} className="click" onClick={() => nav(`/segments/${s.key}`)}>
                    <td><strong>{s.name}</strong><div className="small muted"><code>{s.key}</code></div></td>
                    <td title={s.last_refreshed_at ? t('посчитано {when}', { when: ago(s.last_refreshed_at) }) : t('ещё не пересчитывался')}>{s.last_count == null ? '—' : num(s.last_count)}</td>
                    <td className="small muted">{s.revision}</td>
                    <td className="small muted">{ago(s.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

export function SegmentEditorPage() {
  const { key: routeKey = 'new' } = useParams();
  const isNew = routeKey === 'new';
  const { me } = useSession();
  const nav = useNavigate();
  const toast = useToast();
  const writer = canWrite(me) && !me?.support;
  const schema = useAsync(() => api.get<{ items: AttributeDef[] }>('/contacts/attributes'));
  const segments = useAsync(() => api.get<Segment[]>('/segments'));
  const existing = useAsync(() => (isNew ? Promise.resolve(null) : api.get<Segment>(`/segments/${routeKey}`)), [routeKey]);
  const keys = schema.data?.items ?? [];
  const types = useMemo(() => new Map(keys.map((k) => [k.key, k.type])), [keys]);

  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [group, setGroup] = useState<Group>({ t: 'group', kind: 'all', items: [] });
  const [tab, setTab] = useState<'builder' | 'json'>('builder');
  const [jsonText, setJsonText] = useState('');
  const [jsonOnly, setJsonOnly] = useState(false);
  const [preview, setPreview] = useState<SegmentPreview | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const s = existing.data;
    if (!s) return;
    setName(s.name);
    setKey(s.key);
    const g = fromJson(s.conditions);
    if (g) setGroup(g);
    else {
      setJsonOnly(true);
      setTab('json');
    }
    setJsonText(JSON.stringify(s.conditions, null, 2));
  }, [existing.data]);

  const conditions = (): unknown => {
    if (jsonOnly || tab === 'json') return JSON.parse(jsonText);
    return toJson(group, types);
  };

  async function count() {
    setError('');
    try {
      setPreview(await api.post<SegmentPreview>('/segments/preview', { conditions: conditions() }));
    } catch (err) {
      setPreview(null);
      setError(err instanceof SyntaxError ? t('JSON не разбирается') : errorText(err));
    }
  }
  async function save() {
    setError('');
    setBusy(true);
    try {
      const k = isNew ? key.trim() : routeKey;
      const saved = await api.put<Segment>(`/segments/${encodeURIComponent(k)}`, { name: name.trim(), conditions: conditions(), ...(existing.data ? { expected_revision: existing.data.revision } : {}) });
      toast(t('Сегмент сохранён'));
      if (isNew) nav(`/segments/${saved.key}`, { replace: true });
      else existing.reload();
    } catch (err) {
      setError(err instanceof SyntaxError ? t('JSON не разбирается') : errorText(err));
    } finally {
      setBusy(false);
    }
  }
  async function remove() {
    if (!confirm(t('Удалить сегмент «{name}»?', { name }))) return;
    try {
      await api.del(`/segments/${encodeURIComponent(routeKey)}`);
      nav('/segments');
    } catch (err) {
      toast(errorText(err), 'bad');
    }
  }
  function switchTab(mode: 'builder' | 'json') {
    if (mode === 'json') setJsonText(JSON.stringify(conditions(), null, 2));
    if (mode === 'builder') {
      try {
        const g = fromJson(JSON.parse(jsonText));
        if (!g) {
          setJsonOnly(true);
          setError(t('Это дерево сложнее конструктора (not, фильтры свойств, часы) — правьте его в JSON.'));
          return;
        }
        setGroup(g);
        setJsonOnly(false);
      } catch {
        setError(t('JSON не разбирается'));
        return;
      }
    }
    setError('');
    setTab(mode);
  }

  if (!isNew && existing.loading) return <div className="stack"><Skeleton h={32} w={300} /><Skeleton h={200} /></div>;
  if (!isNew && !existing.data) return <Empty title={t('Сегмент не найден')} />;

  return (
    <>
      <Link to="/segments" className="btn btn-ghost btn-sm" style={{ marginBottom: 12 }}><IconArrowLeft size={15} /> {t('Все сегменты')}</Link>
      <div className="page-head">
        <div className="grow">
          <h1>{isNew ? t('Новый сегмент') : name || routeKey}</h1>
          {!isNew && existing.data && <p>{t('Ревизия {rev} · ключ', { rev: existing.data.revision })} <code>{existing.data.key}</code></p>}
        </div>
        {!isNew && writer && <button className="btn btn-danger" onClick={remove}><IconTrash size={15} /> {t('Удалить')}</button>}
      </div>
      <div className="grid grid-2">
        <div className="card" style={{ minWidth: 0 }}>
          <div className="card-pad stack">
            <div className="row wrap" style={{ alignItems: 'flex-start' }}>
              <div style={{ flex: 2, minWidth: 200 }}><Field label={t('Название')}><input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('Покупатели за 30 дней')} disabled={!writer} /></Field></div>
              {isNew && <div style={{ flex: 1, minWidth: 160 }}><Field label={t('Ключ')} hint={t('латиница, цифры, - и _')}><input className="input input-mono" value={key} onChange={(e) => setKey(e.target.value.toLowerCase())} placeholder="buyers-30d" /></Field></div>}
            </div>
            <div className="tabs row">
              <button className={`tab${tab === 'builder' ? ' on' : ''}`} onClick={() => switchTab('builder')}>{t('Конструктор')}</button>
              <button className={`tab${tab === 'json' ? ' on' : ''}`} onClick={() => switchTab('json')}>JSON</button>
            </div>
            {tab === 'builder' ? (
              <GroupEditor group={group} onChange={setGroup} keys={keys} segments={(segments.data ?? []).filter((s) => s.key !== routeKey)} depth={0} disabled={!writer} />
            ) : (
              <textarea className="input input-mono" rows={16} value={jsonText} onChange={(e) => setJsonText(e.target.value)} spellCheck={false} aria-label={t('Условия в JSON')} />
            )}
            {error && <div className="callout bad">{error}</div>}
            <div className="row wrap">
              <button className="btn" onClick={count}>{t('Посчитать')}</button>
              {writer && <button className="btn btn-primary" onClick={save} disabled={busy || !name.trim() || (isNew && !key.trim())}>{t('Сохранить')}</button>}
            </div>
          </div>
        </div>
        <div className="stack" style={{ minWidth: 0 }}>
          <div className="card card-pad stack">
            <h3>{t('Кто получит маркетинг')}</h3>
            {!preview ? (
              <p className="small muted">{t('Нажмите «Посчитать»: покажем, сколько контактов подходит и сколько отсеется до отправки — без согласия, отозвавших, подавленных, без адреса.')}</p>
            ) : (
              <>
                <div className="kpi" style={{ padding: 0 }}>
                  <div className="label">{t('получат маркетинг')}</div>
                  <div className="value">{num(preview.eligible)}<small> {t('из {total}', { total: num(preview.count) })}</small></div>
                </div>
                <p className="small">{explain(preview)}</p>
                <table className="table">
                  <tbody>
                    <tr><td>{t('Подходят под условия')}</td><td style={{ textAlign: 'right' }}><strong>{num(preview.count)}</strong></td></tr>
                    <tr><td>{t('без адреса')}</td><td style={{ textAlign: 'right' }}>{minus(preview.excluded.no_email)}</td></tr>
                    <tr><td>{t('в подавлениях')}</td><td style={{ textAlign: 'right' }}>{minus(preview.excluded.suppressed)}</td></tr>
                    <tr><td>{t('отозвали согласие')}</td><td style={{ textAlign: 'right' }}>{minus(preview.excluded.withdrawn)}</td></tr>
                    <tr><td>{t('без согласия')}</td><td style={{ textAlign: 'right' }}>{minus(preview.excluded.no_consent)}</td></tr>
                  </tbody>
                </table>
                {preview.sample.length > 0 && <div className="small muted">{t('Например:')} {preview.sample.map((s) => s.email_masked).join(', ')}</div>}
              </>
            )}
          </div>
          <div className="callout">{t('Сегмент может ссылаться на другой сегмент — только на один уровень. Условия проверяются по схеме атрибутов: оператор должен подходить к типу.')}</div>
        </div>
      </div>
    </>
  );
}

const minus = (n: number) => (n ? `−${num(n)}` : '0');

function explain(p: SegmentPreview): string {
  const parts = [
    p.excluded.no_consent && t('без согласия {n}', { n: num(p.excluded.no_consent) }),
    p.excluded.withdrawn && t('отозвали {n}', { n: num(p.excluded.withdrawn) }),
    p.excluded.suppressed && t('подавлены {n}', { n: num(p.excluded.suppressed) }),
    p.excluded.no_email && t('без адреса {n}', { n: num(p.excluded.no_email) }),
  ].filter(Boolean);
  const details = parts.length ? `: ${parts.join(', ')}` : '';
  return t('Из {total}{details} → получат маркетинг {eligible}.', { total: num(p.count), details, eligible: num(p.eligible) });
}

export function GroupEditor({ group, onChange, keys, segments, depth, disabled, onRemove }: { group: Group; onChange: (g: Group) => void; keys: AttributeDef[]; segments: Segment[]; depth: number; disabled: boolean; onRemove?: () => void }) {
  const set = (i: number, item: Row | Group) => onChange({ ...group, items: group.items.map((x, j) => (j === i ? item : x)) });
  const remove = (i: number) => onChange({ ...group, items: group.items.filter((_, j) => j !== i) });
  return (
    <div className="stack" style={{ gap: 8, ...(depth ? { padding: 12, borderRadius: 12, border: '1px dashed var(--border-strong)' } : {}) }}>
      <div className="row wrap" style={{ gap: 8 }}>
        <div className="chips">
          <button className={`chip${group.kind === 'all' ? ' on' : ''}`} onClick={() => onChange({ ...group, kind: 'all' })} disabled={disabled}>{t('все условия')}</button>
          <button className={`chip${group.kind === 'any' ? ' on' : ''}`} onClick={() => onChange({ ...group, kind: 'any' })} disabled={disabled}>{t('любое условие')}</button>
        </div>
        <span className="spacer" />
        {onRemove && !disabled && <button className="btn btn-sm btn-ghost" onClick={onRemove} aria-label={t('Удалить группу')}><IconTrash size={14} /></button>}
      </div>
      {group.items.length === 0 && <div className="small muted">{t('Пока ни одного условия.')}</div>}
      {group.items.map((item, i) => (
        <div key={i} className="stack" style={{ gap: 6 }}>
          {i > 0 && <div className="small muted" style={{ fontWeight: 700 }}>{group.kind === 'all' ? t('и') : t('или')}</div>}
          {item.t === 'group' ? (
            <GroupEditor group={item} onChange={(g) => set(i, g)} keys={keys} segments={segments} depth={depth + 1} disabled={disabled} onRemove={() => remove(i)} />
          ) : (
            <RowEditor row={item} onChange={(r) => set(i, r)} onRemove={() => remove(i)} keys={keys} segments={segments} disabled={disabled} />
          )}
        </div>
      ))}
      {!disabled && (
        <div className="row wrap" style={{ gap: 8 }}>
          <button className="btn btn-sm" onClick={() => onChange({ ...group, items: [...group.items, newRow(keys)] })}><IconPlus size={14} /> {t('Условие')}</button>
          <button className="btn btn-sm" onClick={() => onChange({ ...group, items: [...group.items, { t: 'event', name: 'order.paid', countOp: 'gte', count: '1', days: '30' }] })}><IconPlus size={14} /> {t('Событие')}</button>
          {depth === 0 && <button className="btn btn-sm" onClick={() => onChange({ ...group, items: [...group.items, { t: 'group', kind: group.kind === 'all' ? 'any' : 'all', items: [newRow(keys)] }] })}><IconPlus size={14} /> {t('Группа')}</button>}
        </div>
      )}
    </div>
  );
}

const KIND_LABEL: Record<Row['t'], string> = { attribute: 'Атрибут', field: 'Поле', consent: 'Согласие', event: 'Событие', segment: 'Сегмент' };

function RowEditor({ row, onChange, onRemove, keys, segments, disabled }: { row: Row; onChange: (r: Row) => void; onRemove: () => void; keys: AttributeDef[]; segments: Segment[]; disabled: boolean }) {
  const kind = (t: Row['t']) => {
    if (t === row.t) return;
    if (t === 'attribute') onChange(newRow(keys).t === 'attribute' ? newRow(keys) : { t: 'attribute', key: '', op: 'eq', value: '' });
    if (t === 'field') onChange({ t: 'field', field: 'email', op: 'contains', value: '' });
    if (t === 'consent') onChange({ t: 'consent', status: 'granted' });
    if (t === 'event') onChange({ t: 'event', name: 'order.paid', countOp: 'gte', count: '1', days: '30' });
    if (t === 'segment') onChange({ t: 'segment', key: segments[0]?.key ?? '' });
  };
  const opSelect = (type: AttributeType, op: Op, set: (op: Op) => void) => (
    <select className="select input" style={{ width: 'auto', minWidth: 140 }} value={op} onChange={(e) => set(e.target.value as Op)} disabled={disabled} aria-label={t('Оператор')}>
      {OPS_BY_TYPE[type].map((o) => <option key={o} value={o}>{t(OP_LABEL[o])}</option>)}
    </select>
  );
  const valueInput = (op: Op, value: string, set: (v: string) => void, type: AttributeType) =>
    NO_VALUE.includes(op) ? null : (
      <input
        className="input"
        style={{ flex: 1, minWidth: 120 }}
        value={value}
        onChange={(e) => set(e.target.value)}
        disabled={disabled}
        aria-label={t('Значение')}
        placeholder={RELATIVE.includes(op) ? t('дней') : op === 'between' ? t('от, до') : LIST.includes(op) ? t('через запятую') : type === 'date' ? 'YYYY-MM-DD' : t('значение')}
      />
    );
  return (
    <div className="row wrap" style={{ gap: 8, alignItems: 'center' }}>
      <select className="select input" style={{ width: 'auto' }} value={row.t} onChange={(e) => kind(e.target.value as Row['t'])} disabled={disabled} aria-label={t('Вид условия')}>
        {(Object.keys(KIND_LABEL) as Row['t'][]).map((k) => <option key={k} value={k}>{t(KIND_LABEL[k])}</option>)}
      </select>
      {row.t === 'attribute' && (() => {
        const type = keys.find((k) => k.key === row.key)?.type ?? 'string';
        return (
          <>
            <select className="select input" style={{ width: 'auto', minWidth: 120 }} value={row.key} onChange={(e) => { const t = keys.find((k) => k.key === e.target.value)?.type ?? 'string'; onChange({ ...row, key: e.target.value, op: OPS_BY_TYPE[t].includes(row.op) ? row.op : OPS_BY_TYPE[t][0] }); }} disabled={disabled} aria-label={t('Атрибут')}>
              {!keys.some((k) => k.key === row.key) && <option value={row.key}>{row.key || '—'}</option>}
              {keys.map((k) => <option key={k.key} value={k.key}>{k.key}</option>)}
            </select>
            {opSelect(type, row.op, (op) => onChange({ ...row, op }))}
            {valueInput(row.op, row.value, (value) => onChange({ ...row, value }), type)}
          </>
        );
      })()}
      {row.t === 'field' && (() => {
        const type = FIELDS.find((f) => f.id === row.field)?.type ?? 'string';
        return (
          <>
            <select className="select input" style={{ width: 'auto' }} value={row.field} onChange={(e) => { const f = FIELDS.find((x) => x.id === e.target.value)!; onChange({ ...row, field: f.id, op: OPS_BY_TYPE[f.type].includes(row.op) ? row.op : OPS_BY_TYPE[f.type][0] }); }} disabled={disabled} aria-label={t('Поле')}>
              {FIELDS.map((f) => <option key={f.id} value={f.id}>{t(f.label)}</option>)}
            </select>
            {opSelect(type, row.op, (op) => onChange({ ...row, op }))}
            {valueInput(row.op, row.value, (value) => onChange({ ...row, value }), type)}
          </>
        );
      })()}
      {row.t === 'consent' && (
        <select className="select input" style={{ width: 'auto' }} value={row.status} onChange={(e) => onChange({ ...row, status: e.target.value as never })} disabled={disabled} aria-label={t('Статус согласия')}>
          <option value="granted">{t('есть согласие на маркетинг')}</option>
          <option value="withdrawn">{t('согласие отозвано')}</option>
          <option value="unknown">{t('записи о согласии нет')}</option>
        </select>
      )}
      {row.t === 'event' && (
        <>
          <input className="input input-mono" style={{ flex: 1, minWidth: 140 }} value={row.name} onChange={(e) => onChange({ ...row, name: e.target.value })} disabled={disabled} aria-label={t('Имя события')} />
          <select className="select input" style={{ width: 'auto' }} value={row.countOp} onChange={(e) => onChange({ ...row, countOp: e.target.value as never })} disabled={disabled} aria-label={t('Сравнение количества')}>
            <option value="gte">{t('не менее')}</option>
            <option value="lte">{t('не более')}</option>
            <option value="eq">{t('ровно')}</option>
          </select>
          <input className="input" style={{ width: 72 }} inputMode="numeric" value={row.count} onChange={(e) => onChange({ ...row, count: e.target.value.replace(/\D/g, '') })} disabled={disabled} aria-label={t('Сколько раз')} />
          <span className="small muted">{t('раз за')}</span>
          <input className="input" style={{ width: 72 }} inputMode="numeric" value={row.days} onChange={(e) => onChange({ ...row, days: e.target.value.replace(/\D/g, '') })} disabled={disabled} placeholder={t('всё')} aria-label={t('Дней')} />
          <span className="small muted">{t('дн.')}</span>
        </>
      )}
      {row.t === 'segment' && (
        <select className="select input" style={{ width: 'auto', minWidth: 160 }} value={row.key} onChange={(e) => onChange({ ...row, key: e.target.value })} disabled={disabled} aria-label={t('Сегмент')}>
          {!segments.some((s) => s.key === row.key) && <option value={row.key}>{row.key || '—'}</option>}
          {segments.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
        </select>
      )}
      {!disabled && <button className="btn btn-sm btn-ghost" onClick={onRemove} aria-label={t('Удалить условие')}><IconTrash size={14} /></button>}
    </div>
  );
}
